import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  LockOutlined as LockOutlinedIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import type { ChatMessage, ConfirmationStatus } from './types';

interface ConfirmationCardProps {
  message: ChatMessage;
  onConfirm?: (toolCallId: string) => Promise<void> | void;
  onReject?: (toolCallId: string) => Promise<void> | void;
}

function formatRemainingTime(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export const ConfirmationCard = ({
  message,
  onConfirm,
  onReject,
}: ConfirmationCardProps) => {
  const meta = message.metadata;
  const toolCallId = meta?.toolCallId;
  const expiresAt = meta?.expiresAt;
  const explicitStatus = meta?.confirmationStatus ?? 'pending';
  const [pendingAction, setPendingAction] = useState<'confirm' | 'reject' | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (explicitStatus !== 'pending' || !expiresAt) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [explicitStatus, expiresAt]);

  useEffect(() => {
    if (explicitStatus !== 'pending') {
      setPendingAction(null);
    }
  }, [explicitStatus]);

  const remainingMs = useMemo(() => {
    if (!expiresAt) {
      return null;
    }

    const expiresAtMs = Date.parse(expiresAt);
    if (Number.isNaN(expiresAtMs)) {
      return null;
    }

    return expiresAtMs - now;
  }, [expiresAt, now]);

  const isExpired = explicitStatus === 'expired'
    || (explicitStatus === 'pending' && remainingMs !== null && remainingMs <= 0);
  const status: ConfirmationStatus = isExpired ? 'expired' : explicitStatus;
  const isBusy = pendingAction !== null;
  const canAct = Boolean(toolCallId) && status === 'pending' && !isBusy;

  const statusColor = {
    pending: 'warning' as const,
    confirmed: 'success' as const,
    rejected: 'error' as const,
    expired: 'default' as const,
    error: 'error' as const,
  }[status];

  const statusLabel = status === 'error'
    ? meta?.confirmationErrorMessage ?? 'Action failed'
    : status === 'rejected'
      ? 'Cancelled'
      : status.charAt(0).toUpperCase() + status.slice(1);

  const handleConfirm = async () => {
    if (!toolCallId || !canAct) {
      return;
    }

    setPendingAction('confirm');
    try {
      await onConfirm?.(toolCallId);
    } finally {
      setPendingAction(null);
    }
  };

  const handleReject = async () => {
    if (!toolCallId || !canAct) {
      return;
    }

    setPendingAction('reject');
    try {
      await onReject?.(toolCallId);
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <Box sx={{ my: 0.75, ml: 2 }}>
      <Box
        sx={{
          maxWidth: '85%',
          borderRadius: 2,
          border: '1px solid',
          borderColor: status === 'pending' ? 'warning.main' : 'divider',
          backgroundColor: 'background.paper',
          boxShadow: 1,
          p: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LockOutlinedIcon fontSize="small" color={status === 'pending' ? 'warning' : 'action'} />
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
              Confirmation required
            </Typography>
          </Box>
          <Chip
            label={statusLabel}
            size="small"
            color={statusColor}
            variant={status === 'pending' ? 'filled' : 'outlined'}
          />
        </Box>

        <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 1 }}>
          {meta?.confirmDescription ?? 'This action requires confirmation.'}
        </Typography>

        {remainingMs !== null && status === 'pending' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, color: 'text.secondary' }}>
            <ScheduleIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption">
              Expires in {formatRemainingTime(remainingMs)}
            </Typography>
          </Box>
        )}

        {status === 'expired' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            This confirmation request has expired.
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={!canAct}
            onClick={() => {
              void handleConfirm();
            }}
            sx={{ textTransform: 'none', fontSize: 12, minWidth: 88 }}
          >
            {pendingAction === 'confirm' ? <CircularProgress size={14} color="inherit" /> : 'Confirm'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            disabled={!canAct}
            onClick={() => {
              void handleReject();
            }}
            sx={{ textTransform: 'none', fontSize: 12, minWidth: 88 }}
          >
            {pendingAction === 'reject' ? <CircularProgress size={14} color="inherit" /> : 'Cancel'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
